import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { useLoaderData } from "react-router";
import { ISiteSettingsForm } from "../../types/forms";
import { updateSettings } from "../../lib/api";
import { alertActions } from "../../features/alert/alertSlice";
import { store } from "../..";
import {
    globalActions,
    selectSetting,
} from "../../features/global/globalSlice";
import TinyEditor from "../../components/Editor";
import { processTinyData } from "../../lib/utils";

export const loader = async ({ content }: any) => {
    const settings = store.getState().global.settings;
    const settingsPromise = settings.length
        ? Promise.resolve()
        : store.dispatch(globalActions.getSettings());

    await Promise.resolve(settingsPromise);

    return {
        content,
    };
};

const RulesSettings = (): React.JSX.Element | undefined => {
    const dispatch = useAppDispatch();

    const { content: settingName } = useLoaderData() as {
        content: string;
    };

    const settings = useAppSelector(state => state.global.settings);
    const settingText = useAppSelector(state =>
        selectSetting(state, settingName),
    );

    const [editorValue, setEditorValue] = useState<string>(
        settingText?.setting,
    );

    useEffect(() => {
        setEditorValue(settingText?.setting);
    }, [settingName]);

    const formFieldName = `rules.${settingName}` as `rules.${string}`;
    const settingsToShow = [settingName];

    const methods = useForm<Partial<ISiteSettingsForm>>({
        mode: "onChange",
        defaultValues: {
            rules: {
                ...settingsToShow.reduce(
                    (acc, settingName) => {
                        const setting = settings.find(
                            s => s.settingName === settingName,
                        );
                        if (setting) {
                            acc[settingName] = setting.setting;
                        }
                        return acc;
                    },
                    {} as Record<string, any>,
                ),
            },
        },
    });

    const watchSettingText = methods.watch(
        formFieldName,
        methods.getValues(formFieldName),
    );

    const onSubmit: SubmitHandler<Partial<ISiteSettingsForm>> = async data => {
        const dirtyFields = methods.formState.dirtyFields;

        const filteredData: Partial<ISiteSettingsForm> = {
            rules: {},
        };

        (Object.keys(dirtyFields) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                const dirtySubfields = dirtyFields[category];
                Object.keys(dirtySubfields).forEach(subfield => {
                    if (data[category]?.hasOwnProperty(subfield)) {
                        filteredData[category][subfield] = processTinyData(
                            data[category][subfield],
                        );
                    }
                });
            },
        );

        (Object.keys(filteredData) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                if (Object.keys(filteredData[category]).length === 0) {
                    delete filteredData[category];
                }
            },
        );

        await updateSettings(filteredData)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Settings updated successfully",
                        dismissible: true,
                    }),
                );

                methods.reset(data);
            })
            .catch(e =>
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating the settings.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                ),
            );
    };

    return (
        <section className="siteSettingsRulesContent">
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
                <Row>
                    <Col>
                        <h4>{settingText?.displayTitle}</h4>
                        <Form.Control
                            type="hidden"
                            {...methods.register(formFieldName, {
                                value: watchSettingText
                                    ? "true"
                                    : methods.getValues(formFieldName) ||
                                      settingText,
                            })}
                        />
                        <TinyEditor
                            editorValue={editorValue}
                            showSave={false}
                            formField={formFieldName}
                            formMethods={methods}
                            setEditorValue={setEditorValue}
                            key={settingName}
                        />
                    </Col>
                </Row>
                <Button variant="primary" type="submit">
                    Update Settings
                </Button>
            </Form>
        </section>
    );
};

export default RulesSettings;
